import { styled } from '@mui/material/styles';
import { InsightSummaryType } from '../../queries/useInsightsSummaryQuery';
import { Box, Typography, Tooltip, IconButton, Collapse } from '@mui/material';
import { VIEW_ARTICLE } from 'constants/routes';
import { Link, useLocation } from 'react-router-dom';
import CheckFillIcon from 'remixicon-react/CheckFillIcon';
import FileCopyLineIcon from 'remixicon-react/FileCopyLineIcon';
import RenderInsightDescription from './RenderInsightDescription';
import { useMemo, useRef, useState } from 'react';
import { formatDate, highlightSearchkeyWord } from 'utility/helpers';
import SelectMenu from 'components/common/menu/SelectMenu';
import MoreFillIcon from 'remixicon-react/MoreFillIcon';
import FileListLineIcon from 'remixicon-react/FileListLineIcon';
import { theme } from 'config/theme';
import LightbulbFillIcon from 'remixicon-react/LightbulbFillIcon';
import useInsightsStore from 'store/useInsightsStore';
import useUserData from 'hooks/useUserData';
import { MenuItemType } from 'components/drawer/RenderMenu';
import InsightSummaryModal from '../InsightSummaryForm/InsightSummaryModal';
import EditLineIcon from 'remixicon-react/EditLineIcon';
import { INDUSTRY_INSIGHT_COMPANY_ID, INDUSTRY_INSIGHTS } from '../../constants';

type RenderInsightsSummaryProps = {
  expandedInsightSummaryIds: string[];
  insightSummary: InsightSummaryType;
  groupBy: string;
  parentInsightTitle: string;
  searchKeyword: string;
  onUpdateCallback?: () => void;
};

function RenderInsightSummary({
  expandedInsightSummaryIds,
  insightSummary,
  groupBy,
  parentInsightTitle,
  searchKeyword,
  onUpdateCallback,
}: RenderInsightsSummaryProps) {
  const { isNovaOrCompanyAdmin } = useUserData();
  const { setExpandedInsightSummaryIds } = useInsightsStore();
  const { pathname } = useLocation();
  const [openTooltip, setOpenTooltip] = useState(false);
  const [openEditInsightModal, setOpenEditInsightModal] = useState(false);
  const descriptionRef = useRef<HTMLDivElement | null>(null);
  const menu: MenuItemType[] = useMemo(
    () =>
      [
        {
          title: 'Edit',
          icon: <EditLineIcon size={17} />,
          onClick: () => setOpenEditInsightModal(true),
          isHidden: !isNovaOrCompanyAdmin || pathname.includes('project'),
        },
      ].filter((item) => !item.isHidden),
    [isNovaOrCompanyAdmin, pathname],
  );

  const copyInsightSummary = async () => {
    try {
      if (descriptionRef?.current?.innerText) {
        setOpenTooltip(true);
        await navigator.clipboard.writeText(descriptionRef?.current?.innerText);

        setTimeout(() => {
          setOpenTooltip(false);
        }, 1200);
      }
    } catch (error) {
      console.warn('COPY_INSIGHT_SUMMARY_ERROR', error);
    }
  };

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
        <InsightTitle
          mr={0.5}
          mt={0.75}
          sx={{ fontWeight: expandedInsightSummaryIds.includes(insightSummary.id) ? 700 : 500 }}
          onClick={() => {
            setExpandedInsightSummaryIds({
              type: pathname.includes('project') ? 'projectInsightsState' : 'insightsState',
              insightTitle: parentInsightTitle,
              groupBy,
              expandedInsightSummaryIds: expandedInsightSummaryIds.includes(insightSummary.id)
                ? expandedInsightSummaryIds.filter(
                    (insightSummaryId) => insightSummaryId !== insightSummary.id,
                  )
                : [...expandedInsightSummaryIds, insightSummary.id],
            });
          }}
        >
          {insightSummary.title}
        </InsightTitle>
        <Box display="flex" alignItems="center">
          {insightSummary?.companyId === INDUSTRY_INSIGHT_COMPANY_ID && (
            <IconButton title={INDUSTRY_INSIGHTS} size="small" sx={{ mr: 0.5 }}>
              <LightbulbFillIcon size={20} color={theme.palette.custom.pirateGold} />
            </IconButton>
          )}
          {!pathname.includes('project') &&
            (insightSummary?.createdAt || insightSummary?.publishedAt) && (
              <InsightDate variant="caption">
                {formatDate(insightSummary?.createdAt || insightSummary?.publishedAt)}
              </InsightDate>
            )}
          {menu.length > 0 && (
            <SelectMenu menu={menu}>
              <Box>
                <IconButton size="small">
                  <MoreFillIcon />
                </IconButton>
              </Box>
            </SelectMenu>
          )}
        </Box>
      </Box>
      <Collapse in={expandedInsightSummaryIds.includes(insightSummary.id)}>
        <RenderInsightDescription
          ref={descriptionRef}
          searchKeyword={searchKeyword}
          description={insightSummary.description || ''}
        />
        <Box display="flex" alignItems="center" justifyContent="space-between" mt={1}>
          {insightSummary?.companyId === INDUSTRY_INSIGHT_COMPANY_ID ? (
            <BuildWiseInsightsBadge>
              <LightbulbFillIcon size={20} color={theme.palette.custom.pirateGold} />
              &nbsp;{INDUSTRY_INSIGHTS}
            </BuildWiseInsightsBadge>
          ) : (
            (insightSummary?.refArticleId || insightSummary?.articleInfo?.id) &&
            insightSummary?.articleInfo?.title && (
              <SourceContainer maxWidth="90%">
                <FileListLineIcon size={16} color={theme.palette.custom.link} />
                <ArticleLink
                  title={insightSummary?.articleInfo?.title}
                  to={VIEW_ARTICLE.replace(
                    ':articleId',
                    insightSummary.refArticleId || insightSummary?.articleInfo?.id,
                  )}
                  dangerouslySetInnerHTML={{
                    __html: highlightSearchkeyWord(
                      insightSummary?.articleInfo.title,
                      searchKeyword,
                    ),
                  }}
                />
              </SourceContainer>
            )
          )}
          <Tooltip open={openTooltip} placement="top" title={<Typography>Copied</Typography>}>
            <IconButton className="copy-summary" onClick={copyInsightSummary}>
              {openTooltip ? <CheckFillIcon size={20} /> : <FileCopyLineIcon size={20} />}
            </IconButton>
          </Tooltip>
        </Box>
      </Collapse>

      {openEditInsightModal && (
        <InsightSummaryModal
          edit
          open={openEditInsightModal}
          insightSummary={insightSummary}
          onUpdateCallback={onUpdateCallback}
          setOpen={setOpenEditInsightModal}
        />
      )}
    </>
  );
}

const InsightTitle = styled(Typography)(({ theme }) => ({
  width: 'fit-content',
  wordWrap: 'break-word',
  fontSize: 16,
  '& .highlight': {
    backgroundColor: 'yellow !important',
  },
  '& a': {
    color: theme.palette.custom.link,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
}));

const SourceContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  backgroundColor: theme.palette.custom.whiteSmoke,
  padding: '4px 0',
  borderRadius: 8,
  marginRight: 16,
}));

const ArticleLink = styled(Link)(({ theme }) => ({
  fontSize: 14,
  marginLeft: 4,
  color: theme.palette.custom.link,
  width: '100%',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  userSelect: 'auto',
  '&:hover': {
    textDecoration: 'underline',
  },
}));

export const InsightDate = styled(Typography)({
  fontSize: 12,
  fontWeight: 400,
  whiteSpace: 'nowrap',
  marginRight: 8,
});

const BuildWiseInsightsBadge = styled(Box)(({ theme }) => ({
  color: theme.palette.custom.brown,
  backgroundColor: theme.palette.custom.earlyDawn,
  padding: '4px 8px',
  fontSize: 14,
  display: 'flex',
  alignItems: 'center',
  borderRadius: 8,
}));

export default RenderInsightSummary;
